import { useCallback, useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { thicknessReviewSchema } from '@/features/cad/schemas/thicknessReview.schema';
import {
  getThicknessReview,
  updateThicknessReview,
} from '../../../services/cad.service';
import {
  CADModel,
  ThicknessAnalysis,
  ThicknessRegionDisposition,
  ThicknessRegionReview,
  ThicknessReview,
  ThicknessReviewStatus,
} from '../../../types/cad';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';

interface UseThicknessReviewArgs {
  model: CADModel | null;
  displayedAnalysis: ThicknessAnalysis | null;
  t: TFunction;
  setError: (message: string | null) => void;
}

/** Loads and persists the reviewer decision for the displayed analysis. */
export const useThicknessReview = ({
  model,
  displayedAnalysis,
  t,
  setError,
}: UseThicknessReviewArgs) => {
  const [review, setReview] = useState<ThicknessReview | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [regionDispositions, setRegionDispositions] = useState<
    Record<string, ThicknessRegionDisposition>
  >({});
  const [regionReviews, setRegionReviews] = useState<
    Record<string, ThicknessRegionReview>
  >({});
  const [savingReview, setSavingReview] = useState(false);

  const reviewDirty = Boolean(
    review
    && (
      reviewComment !== review.comment
      || JSON.stringify(regionDispositions)
        !== JSON.stringify(review.regionDispositions)
      || JSON.stringify(regionReviews) !== JSON.stringify(review.regionReviews)
    ),
  );

  useEffect(() => {
    if (
      !model
      || displayedAnalysis?.status !== 'completed'
      || displayedAnalysis.modelId !== model.id
    ) {
      setReview(null);
      setReviewComment('');
      setRegionDispositions({});
      setRegionReviews({});
      return;
    }
    let cancelled = false;
    getThicknessReview(model.id, displayedAnalysis.analysisId)
      .then((loadedReview) => {
        if (cancelled) return;
        setReview(loadedReview);
        setReviewComment(loadedReview.comment);
        setRegionDispositions(loadedReview.regionDispositions);
        setRegionReviews(loadedReview.regionReviews || {});
      })
      .catch((reviewError) => {
        if (cancelled) return;
        setReview(null);
        setReviewComment('');
        setRegionDispositions({});
        setRegionReviews({});
        setError(
          resolveApiErrorMessage(t, reviewError, {
            fallbackKey: 'cad.thickness.review.loadFailed',
          }),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [
    displayedAnalysis?.analysisId,
    displayedAnalysis?.modelId,
    displayedAnalysis?.status,
    model,
    setError,
    t,
  ]);

  const saveReview = useCallback(async (status: ThicknessReviewStatus) => {
    if (!model || !displayedAnalysis || !review) return;
    // Values the API rejects with a 422 are blocked here instead, so the reviewer
    // gets the specific limit rather than the generic saveFailed banner.
    const parsed = thicknessReviewSchema.safeParse({
      comment: reviewComment,
      regionReviews,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    setSavingReview(true);
    try {
      const saved = await updateThicknessReview(
        model.id,
        displayedAnalysis.analysisId,
        {
          status,
          regionDispositions,
          regionReviews,
          comment: reviewComment,
          updatedAt: review.updatedAt,
        },
      );
      setReview(saved);
      setReviewComment(saved.comment);
      setRegionDispositions(saved.regionDispositions);
      setRegionReviews(saved.regionReviews || {});
      setError(null);
    } catch (reviewError) {
      const isConflict = (
        typeof reviewError === 'object'
        && reviewError !== null
        && 'code' in reviewError
        && reviewError.code === 'HTTP_409'
      );
      if (isConflict) {
        try {
          const current = await getThicknessReview(
            model.id,
            displayedAnalysis.analysisId,
          );
          setReview(current);
          setReviewComment(current.comment);
          setRegionDispositions(current.regionDispositions);
          setRegionReviews(current.regionReviews || {});
        } catch {
          // Preserve the conflict message even when the follow-up reload fails.
        }
        setError(t('cad.thickness.review.conflict'));
        return;
      }
      setError(
        resolveApiErrorMessage(t, reviewError, {
          fallbackKey: 'cad.thickness.review.saveFailed',
        }),
      );
    } finally {
      setSavingReview(false);
    }
  }, [
    displayedAnalysis,
    model,
    regionDispositions,
    regionReviews,
    review,
    reviewComment,
    setError,
    t,
  ]);

  return {
    review,
    reviewComment,
    setReviewComment,
    regionDispositions,
    setRegionDispositions,
    regionReviews,
    setRegionReviews,
    savingReview,
    reviewDirty,
    saveReview,
  };
};
